/**
 * Business Profile writes (XC-ERP1-U3 §5.1; ERP1-P2-GW's `POST /api/v1/business-profile/facts`;
 * the page side is ERP1-P2-BP-EDIT).
 *
 * A save names only the fact (`kind` + `subtype`), its value and the page's save id. It never names
 * the chain (the server always writes the fact's own chain), the tenant, the acting user, the
 * principal, the channel, the words shown or the subjects: the server takes the tenant and the user
 * from the JWT and derives the rest from the value, so none can disagree with what was entered. The
 * server refuses a body with any other key (`extra="forbid"`).
 *
 * A save also names the page answer it was made from (`based_on`, required, null when the page
 * showed none): if another admin changed the answer meanwhile, the server refuses it (409
 * `fact_changed_since_opened`) and files nothing.
 *
 * Undo (`POST /facts/{ruling_ref}/undo`) names the save it undoes (`undoes`: that save's
 * `ui_event_id`). It applies only while that save is still the answer, and restores what the save
 * replaced (XC-BP1-W1 §1, "The write contract").
 */

import type { ProductEntry } from '@/types/businessProfile/products';
import type { VendorEntry } from '@/types/businessProfile/vendorList';
import type { HoldRuleValue } from '@/types/businessProfile/dataHandling';
import type { DryRunReviewValue } from '@/types/businessProfile/dryRunReview';
import type { ProductWordsValue } from '@/types/businessProfile/productWords';
import type { WhoSeesWhatValue } from '@/types/businessProfile/whoSeesWhat';

import { businessProfileApi } from './businessProfileClient';

export const BUSINESS_PROFILE_FACTS_PATH = '/api/v1/business-profile/facts';

/** `self_identity/owner` (XC-BP1-W1 §1): exactly what the owner entered, trimmed. */
export interface OwnerFactValue {
  owner_name: string;
  /** Required: the executor binds the owner by it. */
  owner_email: string;
  /** Only ever what the owner typed (or saved before): Wingo never fills it in. */
  legal_name: string | null;
  /** "Names you go by". */
  names: string[];
  /** "Email addresses that are yours". */
  emails: string[];
  /** "Names that are not you": another person with a similar name. Always sent, `[]` when none. */
  excluded_names: string[];
  /**
   * The QuickBooks names he said are him ("Yes, that's me", ERP1-P2-U3-OWNER-ITS-ME), each one of
   * his names above. Sent only when there is one: the server omits the key when empty.
   */
  names_on_quickbooks?: string[];
}

/** The owner row's save (XC-BP1-W1 §1). */
export interface OwnerFactWrite {
  kind: 'self_identity';
  subtype: 'owner';
  value: OwnerFactValue;
  /** One per save attempt; a retry of the same save sends the same id (the server replays it). */
  ui_event_id: string;
  /** The ledger row of the page answer the editor opened with (`evidence[0].id`), or null. */
  based_on: string | null;
}

/**
 * The company's names (XC-BP1-W2 §3 shapes; "Your company", F1): each list exactly as he entered it,
 * trimmed. `not_ours` is always sent (`[]` when none): the answer's own strikes, plus (R5) each value
 * the engine counted that he took off with its × (the server applies a customer's strike).
 * `also_on_records` (his "Yes, that's us" to the executor's question when another record carries a
 * value he gave) is sent only when there is one, each entry a value of the list.
 */
export interface LegalNameFactValue {
  /** At least one: a company always has a legal name. */
  legal_names: string[];
  not_ours: string[];
  also_on_records?: string[];
}

export interface DomainsFactValue {
  /** Bare hosts as the engine keys them ("jesshalls.com"): lower-cased, no scheme, no path. */
  domains: string[];
  not_ours: string[];
  also_on_records?: string[];
}

/** "Other names you trade under" (`self_identity/aliases`): the legal name's shape, `names` for its list. */
export interface AliasesFactValue {
  names: string[];
  not_ours: string[];
  also_on_records?: string[];
  /** His "Both are my company" over earlier answers that two names are different (board s1325-1):
   * each entry is that answer's own two spellings. Omitted when empty. */
  replaces_answers?: [string, string][];
}

interface CompanyFactWriteOf<S extends string, V> {
  kind: 'self_identity';
  subtype: S;
  value: V;
  /** One per changed fact per save attempt; a retry of the same change sends the same id. */
  ui_event_id: string;
  /** The ledger row of the answer the editor opened with (the target's `based_on`), or null. */
  based_on: string | null;
}

/** One company-names fact the editor saves: only the facts he changed, all in one request. */
export type CompanyNamesFactWrite =
  | CompanyFactWriteOf<'legal_name', LegalNameFactValue>
  | CompanyFactWriteOf<'domains', DomainsFactValue>
  | CompanyFactWriteOf<'aliases', AliasesFactValue>;

/**
 * R5: one company he ran before (`self_identity/predecessor`): its name, whether it is no longer
 * trading (`inactive`) and whether its records stay a vendor's, never merged into his company
 * (`keep_as_vendor`).
 */
export interface PreviousCompany {
  name: string;
  /** His QuickBooks vendor number for it, when the answer named one (the pre-filled ruling's): kept as is. */
  quickbooks_vendor?: string | null;
  /** Always true (board s1323-4 (a)): a previous company's records stay inactive, kept as vendors. */
  inactive: boolean;
  keep_as_vendor: boolean;
}

/** "Previous company" (`self_identity/predecessor`): every company he ran before, as he left them. */
export interface PredecessorFactValue {
  companies: PreviousCompany[];
}

/** The previous companies fact, saved in the same request as the company's names. */
export type PredecessorFactWrite = CompanyFactWriteOf<'predecessor', PredecessorFactValue>;

/** One evidence row the Suggested row showed: its table and id, as the read model sent them. */
export interface ShownEvidence {
  table: string;
  id: string;
}

/**
 * A tap on a Suggested row (XC-BP1-W2 §3, ERP1-P2-BP-CONFIRM-ROUTE): the suggestion it answers
 * (`proposal_ref`), how (`stance`), the value the button said it files, sent back verbatim, and the
 * evidence rows the row showed, in order. The server rebuilds the suggestion and files nothing when
 * any of it changed (409 `proposal_changed_since_shown`).
 */
export interface ConfirmFactWrite {
  kind: string;
  subtype: string;
  value: unknown;
  ui_event_id: string;
  /** Always null: a suggestion shows only when the row holds no answer. */
  based_on: null;
  proposal_ref: string;
  stance: 'confirm';
  shown_evidence: ShownEvidence[];
}

/**
 * His vendor list (XC-INV2PO DB6, `business_policy/vendor_per_material`): the whole list each save,
 * every row as the page read it with the vendors he chose (`businessProfileVendors.listValue`).
 */
/** AW-144: his "Your products" list (`business_policy/product_holds`), sent whole with each save. */
export interface ProductHoldsFactWrite {
  kind: 'business_policy';
  subtype: 'product_holds';
  value: { entries: ProductEntry[] };
  ui_event_id: string;
  based_on: string | null;
}

export interface VendorListFactWrite {
  kind: 'business_policy';
  subtype: 'vendor_per_material';
  value: { entries: VendorEntry[] };
  ui_event_id: string;
  based_on: string | null;
}

/**
 * His "Held from the outside document reader" rule (XC-DATA-HANDLING-1,
 * `business_policy/external_parse_hold`): the whole rule each save, under the engine's own arm names.
 */
export interface HoldRuleFactWrite {
  kind: 'business_policy';
  subtype: 'external_parse_hold';
  value: HoldRuleValue;
  ui_event_id: string;
  based_on: string | null;
}

/**
 * His review of the dry-run report (XC-INV2PO AC11, `business_policy/inv2po_dry_run_review`): "looks right" or his
 * corrections, citing the report the page showed by its stem and the sha256 of its JSON.
 */
export interface DryRunReviewFactWrite {
  kind: 'business_policy';
  subtype: 'inv2po_dry_run_review';
  value: DryRunReviewValue;
  ui_event_id: string;
  based_on: string | null;
}

/**
 * His "Who sees what" (XC-WHO-SEES-WHAT W3, `business_policy/who_sees_what`): the whole matrix and the masking switch
 * each save. Owner-only: the server refuses any other role 403.
 */
export interface WhoSeesWhatFactWrite {
  kind: 'business_policy';
  subtype: 'who_sees_what';
  value: WhoSeesWhatValue;
  ui_event_id: string;
  based_on: string | null;
}

/**
 * His "Words that tell your products apart" (XC-ERP1-U7-PRODUCT-WORDS, `business_policy/product_words`): the whole
 * list each save. The owner or an admin may save it.
 */
export interface ProductWordsFactWrite {
  kind: 'business_policy';
  subtype: 'product_words';
  value: ProductWordsValue;
  ui_event_id: string;
  based_on: string | null;
}

/** One fact the page saves. */
export type FactWrite =
  | OwnerFactWrite
  | CompanyNamesFactWrite
  | PredecessorFactWrite
  | ConfirmFactWrite
  | VendorListFactWrite
  | ProductHoldsFactWrite
  | HoldRuleFactWrite
  | DryRunReviewFactWrite
  | WhoSeesWhatFactWrite
  | ProductWordsFactWrite;

export interface FactsWriteRequest {
  facts: FactWrite[];
}

export interface FactWriteResult {
  ruling_ref: string;
  state: string;
  /** The server had already filed this `ui_event_id`: nothing new was written. */
  replayed: boolean;
  /** The save replaced an earlier answer (one given on the page or by email). */
  replaced_earlier_answer: boolean;
}

export async function saveBusinessProfileFacts(body: FactsWriteRequest): Promise<FactWriteResult[]> {
  const { data } = await businessProfileApi.post<{ facts: FactWriteResult[] }>(
    BUSINESS_PROFILE_FACTS_PATH,
    body,
  );
  return data.facts;
}

/** The undo route for one page answer, by its `ruling_ref` (a path segment, so encoded). */
export const undoPath = (rulingRef: string): string =>
  `${BUSINESS_PROFILE_FACTS_PATH}/${encodeURIComponent(rulingRef)}/undo`;

/** Undo the save `undoes` (its `ui_event_id`) on a page answer, with this undo's own save id. */
export async function undoBusinessProfileFact(
  rulingRef: string,
  uiEventId: string,
  undoes: string,
): Promise<FactWriteResult> {
  const { data } = await businessProfileApi.post<FactWriteResult>(undoPath(rulingRef), {
    ui_event_id: uiEventId,
    undoes,
  });
  return data;
}
